import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useRouter } from 'next/router';
import styled from 'styled-components';
import { Toggle } from '../../ui/Toggle';
import { SquareFilterEnum } from '../../Views/ResidencesPage/types';
import { ResidenceSort } from '../../../graphql/graphql';
import { normalizeResidencesSortForUrl } from '../../../utils/helpers/normalizeResidencesSortForUrl';
import { animation } from '../../../styled/placeholders.styled';
import { Language } from '../../../utils/types';
import { getClearAsPath } from '../../../utils/helpers/seo';

interface SquareFilterProps {
  selectedValue: SquareFilterEnum;
  sortingValue: ResidenceSort;
  onChange: React.Dispatch<React.SetStateAction<SquareFilterEnum>>;
}

const Placeholder = styled.div<{ $isRu: boolean }>`
  ${animation};
  height: 50px;
  width: ${({ $isRu }) => ($isRu ? '234px' : '257px')};
  border-radius: 14px;
`;

export const SquareFilterPlaceholder: React.FC = () => {
  const { i18n } = useTranslation();

  return <Placeholder $isRu={i18n.language === Language.ru} />;
};

export const SquareFilter: React.FC<SquareFilterProps> = ({
  selectedValue,
  sortingValue,
  onChange
}) => {
  const { t } = useTranslation();
  const router = useRouter();

  const filterOptions = useMemo(
    () => [
      { id: SquareFilterEnum.perSqr, name: t('residences.price_per_unit') },
      { id: SquareFilterEnum.all, name: t('residences.price_all') }
    ],
    [t]
  );

  const handleChange = (valueId: SquareFilterEnum) => {
    onChange(valueId);

    const sortValue = normalizeResidencesSortForUrl(valueId, sortingValue);

    if (sortValue) {
      router.push(
        {
          pathname: getClearAsPath(router.asPath),
          query: {
            ...router.query,
            sort: sortValue.toLowerCase()
          }
        },
        undefined,
        { shallow: true }
      );
    }
  };

  return (
    <Toggle
      options={filterOptions}
      selectedValue={selectedValue}
      onChange={handleChange}
      cypress="square-filter"
      stat="toggle-price"
    />
  );
};
